import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../lib/cart";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  return (
    <motion.div
      className="fixed inset-0 z-[80]"
      initial={false}
      animate={{ pointerEvents: open ? "auto" : "none" }}
    >
      <motion.div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        onClick={() => setOpen(false)}
      />
      <motion.aside
        data-testid="cart-drawer"
        className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0E1015] border-l border-white/10 flex flex-col"
        initial={{ x: "100%" }}
        animate={{ x: open ? 0 : "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
          <span className="font-mono text-[11px] tracking-[0.25em] text-dim">YOUR CART / {String(items.length).padStart(2, "0")} ITEMS</span>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} aria-label="Close cart" className="text-dim hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <p className="font-mono text-[11px] tracking-widest text-mute pt-10 text-center">
              YOUR CART IS EMPTY — THE DROP WON'T WAIT.
            </p>
          )}
          {items.map((it, i) => (
            <div key={`${it.productId}-${it.color}-${it.size}`} className="flex gap-4 border border-white/[0.08] bg-[#151821] p-3">
              <img src={it.image} alt={it.title} className="w-16 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-display text-xs font-bold uppercase truncate">{it.title}</p>
                <p className="font-mono text-[10px] text-dim mt-1 uppercase">
                  {it.color} / {it.size} — ₹{it.price}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center border border-white/15">
                    <button data-testid={`cart-qty-minus-${i}`} onClick={() => updateQty(i, it.qty - 1)} className="px-2 py-1 text-dim hover:text-white"><Minus size={11} /></button>
                    <span className="font-mono text-[11px] px-2">{it.qty}</span>
                    <button data-testid={`cart-qty-plus-${i}`} onClick={() => updateQty(i, it.qty + 1)} className="px-2 py-1 text-dim hover:text-white"><Plus size={11} /></button>
                  </div>
                  <button data-testid={`cart-item-remove-btn-${i}`} aria-label="Remove item" onClick={() => remove(i)} className="text-mute hover:text-volt transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <p className="font-mono text-xs font-bold">₹{(it.price * it.qty).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 px-6 py-5 space-y-3">
          <div className="flex justify-between font-mono text-xs">
            <span className="text-dim tracking-widest">SUBTOTAL</span>
            <span data-testid="cart-subtotal" className="text-base font-bold">₹{subtotal.toLocaleString("en-IN")}</span>
          </div>
          <p className="font-mono text-[10px] text-mute tracking-widest">SHIPPING FREE · COD + ₹49 · TAXES INCLUDED</p>
          <button
            data-testid="cart-checkout-proceed-btn"
            disabled={items.length === 0}
            onClick={() => { setOpen(false); navigate("/checkout"); }}
            className="w-full bg-volt text-void font-display font-bold text-sm tracking-widest py-4 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-acid transition-colors duration-300"
          >
            PROCEED TO CHECKOUT
          </button>
        </div>
      </motion.aside>
    </motion.div>
  );
}
